/**
 * VendorForm page — create/edit vendor with validation, fuzzy match
 * warnings, and AI-assisted document upload for auto-populating
 * vendor fields on create.
 *
 * Requirements: 1.1, 1.5, 19.1, 19.2, 19.3, 19.4, 57.2, 57.3
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorService, type CreateVendorInput, type UpdateVendorInput } from '@/services/vendorService';
import { FormField } from '@/components/ui/FormField';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { AIDocumentUpload } from '@/components/AIDocumentUpload';
import { ConfidenceField } from '@/components/shared/ConfidenceField';
import { useConfidenceConfirmation } from '@/hooks/useConfidenceConfirmation';
import type { ContractSetupResult } from '@/services/aiSetupService';
import type { Vendor, RiskTier } from '@/types';

const RISK_TIERS: RiskTier[] = ['Critical', 'High', 'Medium', 'Low'] as unknown as RiskTier[];
const DATA_HANDLING_OPTIONS = ['PII', 'Financial Data', 'PHI', 'None'];

interface FuzzyMatchWarning {
  fuzzyMatch: boolean;
  warning: string;
  existingVendorName: string;
  existingVendorId: number;
  similarity: number;
}

interface AIFieldMeta {
  fieldName: string;
  confidence: number;
}

export function VendorForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEdit = !!editId;

  const [form, setForm] = useState({
    vendorName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    vendorCategory: '',
    departmentScope: '',
    riskTier: 'Medium' as string,
    dataHandlingClass: 'None' as string,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fuzzyWarning, setFuzzyWarning] = useState<FuzzyMatchWarning | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // AI-assisted setup state
  const [aiResult, setAiResult] = useState<ContractSetupResult | null>(null);
  const [aiFieldMeta, setAiFieldMeta] = useState<AIFieldMeta[]>([]);
  const confidence = useConfidenceConfirmation();

  // Load existing vendor for edit mode
  const { data: existingVendor, isLoading: loadingVendor } = useQuery({
    queryKey: ['vendor', Number(editId)],
    queryFn: () => vendorService.getById(Number(editId)),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingVendor && isEdit) {
      const v = existingVendor as unknown as Vendor;
      setForm({
        vendorName: v.vendorName,
        address: v.address ?? '',
        city: v.city ?? '',
        state: v.state ?? '',
        zipCode: v.zipCode ?? '',
        vendorCategory: v.vendorCategory ?? '',
        departmentScope: v.departmentScope,
        riskTier: v.riskTier,
        dataHandlingClass: v.dataHandlingClass ?? 'None',
      });
    }
  }, [existingVendor, isEdit]);

  // Handle AI extraction result — auto-populate vendor name from contract
  function handleAIExtracted(result: ContractSetupResult) {
    setAiResult(result);
    confidence.resetConfirmations();

    const meta: AIFieldMeta[] = [];
    const updates: Partial<typeof form> = {};

    // Extract vendor name from the contract extraction
    const vendorNameField = result.extractedFields.vendorName;
    if (vendorNameField?.value) {
      // Use normalized name if available, otherwise raw extracted name
      updates.vendorName = result.normalizedVendorName ?? vendorNameField.value;
      meta.push({ fieldName: 'vendorName', confidence: vendorNameField.confidence });
    }

    setForm((f) => ({ ...f, ...updates }));
    setAiFieldMeta(meta);
  }

  function getFieldConfidence(fieldName: string): number | null {
    const meta = aiFieldMeta.find((m) => m.fieldName === fieldName);
    return meta ? meta.confidence : null;
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateVendorInput & { forceCreate?: boolean }) =>
      vendorService.create(input) as Promise<Vendor | FuzzyMatchWarning>,
    onSuccess: (result) => {
      // Check if this is a fuzzy match warning
      if (result && 'fuzzyMatch' in result && (result as FuzzyMatchWarning).fuzzyMatch) {
        setFuzzyWarning(result as FuzzyMatchWarning);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      const vendor = result as Vendor;
      navigate(`/vendors/${vendor.vendorId}`);
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateVendorInput }) =>
      vendorService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor', Number(editId)] });
      navigate(`/vendors/${editId}`);
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.vendorName.trim()) newErrors.vendorName = 'Vendor name is required.';
    if (!form.departmentScope.trim()) newErrors.departmentScope = 'Department scope is required.';
    if (!form.riskTier) newErrors.riskTier = 'Risk tier is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(forceCreate = false) {
    if (!validate()) return;
    setSubmitError(null);
    setFuzzyWarning(null);

    if (isEdit) {
      const vendor = existingVendor as unknown as Vendor;
      updateMutation.mutate({
        id: Number(editId),
        input: {
          vendorName: form.vendorName.trim(),
          address: form.address.trim() || undefined,
          city: form.city.trim() || undefined,
          state: form.state.trim() || undefined,
          zipCode: form.zipCode.trim() || undefined,
          vendorCategory: form.vendorCategory.trim() || undefined,
          departmentScope: form.departmentScope.trim(),
          riskTier: form.riskTier,
          dataHandlingClass: form.dataHandlingClass || undefined,
          updatedAt: vendor.updatedAt,
        },
      });
    } else {
      createMutation.mutate({
        vendorName: form.vendorName.trim(),
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        zipCode: form.zipCode.trim() || undefined,
        vendorCategory: form.vendorCategory.trim() || undefined,
        departmentScope: form.departmentScope.trim(),
        riskTier: form.riskTier,
        dataHandlingClass: form.dataHandlingClass || undefined,
        forceCreate,
      } as CreateVendorInput & { forceCreate?: boolean });
    }
  }

  if (isEdit && loadingVendor) {
    return <LoadingSpinner message="Loading vendor..." />;
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Check if all red-flagged fields are confirmed before allowing save
  const hasUnconfirmedRedFields =
    aiFieldMeta.length > 0 && !confidence.allRedFieldsConfirmed(aiFieldMeta);

  return (
    <div className="max-w-2xl">
      <h1 className="text-lg font-semibold text-text-primary mb-6">
        {isEdit ? 'Edit Vendor' : 'New Vendor'}
      </h1>

      {submitError && (
        <ErrorMessage message={submitError} />
      )}

      {/* AI-Assisted Upload — only on create, not edit */}
      {!isEdit && (
        <AIDocumentUpload
          documentType="contract"
          onExtracted={(result) => handleAIExtracted(result as ContractSetupResult)}
        />
      )}

      {/* Vendor match warning from AI extraction */}
      {aiResult?.vendorMatch?.matchFound && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
        }}>
          <p className="text-sm font-medium text-amber-800">
            ⚠ A similar vendor already exists: <strong>{aiResult.vendorMatch.matchedVendorName}</strong>
            {aiResult.vendorMatch.similarity != null && (
              <> (similarity: {Math.round(aiResult.vendorMatch.similarity * 100)}%)</>
            )}
          </p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => navigate(`/vendors/${aiResult.vendorMatch!.matchedVendorId}`)}
              style={{
                padding: '0.3rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Use Existing Vendor
            </button>
            <button
              type="button"
              onClick={() => setAiResult((prev) => prev ? { ...prev, vendorMatch: null } : null)}
              style={{
                padding: '0.3rem 0.75rem',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Create New Vendor Anyway
            </button>
          </div>
        </div>
      )}

      {/* AI disclaimer */}
      {aiResult?.disclaimer && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: '#0369a1',
        }}>
          {aiResult.disclaimer}
        </div>
      )}

      {/* Fuzzy Match Warning from server-side create */}
      {fuzzyWarning && (
        <div style={{
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
        }}>
          <p className="font-medium text-amber-800">
            {fuzzyWarning.warning}
          </p>
          <p className="mt-2 text-sm text-amber-800">
            Similarity: {Math.round(fuzzyWarning.similarity * 100)}%
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => navigate(`/vendors/${fuzzyWarning.existingVendorId}`)}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              Use Existing Vendor
            </button>
            <button
              onClick={() => handleSubmit(true)}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: '#f59e0b',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              Create Anyway
            </button>
          </div>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Vendor Name — with confidence indicator if AI-populated */}
        {getFieldConfidence('vendorName') !== null ? (
          <ConfidenceField
            label="Vendor Name"
            value={form.vendorName}
            confidence={getFieldConfidence('vendorName')!}
            onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))}
            confirmed={confidence.isFieldConfirmed('vendorName')}
            onConfirm={() => confidence.confirmField('vendorName')}
          />
        ) : (
          <FormField
            label="Vendor Name"
            value={form.vendorName}
            onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))}
            error={errors.vendorName}
            required
          />
        )}

        <div className="grid grid-cols-2 gap-x-4">
          <FormField
            label="Department Scope"
            error={errors.departmentScope}
          >
            <input
              value={form.departmentScope}
              onChange={(e) => setForm((f) => ({ ...f, departmentScope: e.target.value }))}
              placeholder="e.g., IT, Facilities"
              required
              style={{
                width: '100%',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: `1px solid ${errors.departmentScope ? '#ef4444' : '#d1d5db'}`,
                fontSize: '0.875rem',
              }}
            />
          </FormField>

          <FormField label="Risk Tier" error={errors.riskTier}>
            <select
              value={form.riskTier}
              onChange={(e) => setForm((f) => ({ ...f, riskTier: e.target.value }))}
              style={{
                width: '100%',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem',
              }}
            >
              {RISK_TIERS.map((tier) => (
                <option key={String(tier)} value={String(tier)}>{String(tier)}</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField
          label="Category"
          value={form.vendorCategory}
          onChange={(e) => setForm((f) => ({ ...f, vendorCategory: e.target.value }))}
          placeholder="e.g., Telecom, Software, Insurance"
        />

        <FormField
          label="Address"
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
        />

        <div className="grid grid-cols-[2fr_1fr_1fr] gap-x-4">
          <FormField
            label="City"
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
          />
          <FormField
            label="State"
            value={form.state}
            onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
          />
          <FormField
            label="Zip Code"
            value={form.zipCode}
            onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
          />
        </div>

        <FormField label="Data Handling Classification">
          <select
            value={form.dataHandlingClass}
            onChange={(e) => setForm((f) => ({ ...f, dataHandlingClass: e.target.value }))}
            style={{
              width: '100%',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              fontSize: '0.875rem',
            }}
          >
            {DATA_HANDLING_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </FormField>

        <div className="flex gap-2 mt-6">
          <button
            type="submit"
            disabled={isPending || hasUnconfirmedRedFields}
            title={hasUnconfirmedRedFields ? 'Please confirm all red-flagged fields before saving' : undefined}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: hasUnconfirmedRedFields ? '#9ca3af' : '#c8102e',
              color: '#fff',
              cursor: isPending || hasUnconfirmedRedFields ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {isPending ? 'Saving...' : isEdit ? 'Update Vendor' : 'Create Vendor'}
          </button>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/vendors/${editId}` : '/vendors')}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
